import { DownloadForOfflineOutlined } from '@mui/icons-material'
import { Alert, Stack } from '@mui/material'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AxiosError } from 'axios'
import type { DateTime as DateTimeType } from 'luxon'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getCountryAsAlpha2Code } from '@/constants/country'
import { TOMORROW } from '@/constants/dateTime'
import { XLSX_TYPE } from '@/constants/file'
import { useAlertContext } from '@/contexts/AlertContext'
import SubmitButton from '@/features/bidding/components/SubmitButton'
import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import {
  useGetMfrrCapacityBidMutation,
  useIsGettingMfrrCapacityBid,
} from '@/features/bidding/hooks/useGetMfrrCapacityBidMutation'

function ExportMfrrCapacityBid() {
  const { t } = useTranslation()

  const { pushAlert } = useAlertContext()
  const { selectedCountry } = useBiddingContext()
  const isGettingMfrrCapacityBid = useIsGettingMfrrCapacityBid()
  const { getMfrrCapacityBid } = useGetMfrrCapacityBidMutation()

  const [errorMessage, setErrorMessage] = useState<string>('')
  const [bidDate, setBidDate] = useState<DateTimeType>(TOMORROW)

  const dialogProps = {
    title: t('bidding.export_mfrr_bid.dialog.title'),
    description: t('bidding.export_mfrr_bid.dialog.description'),
  }

  const { i18n } = useTranslation()

  async function handleDownloadButtonClick() {
    const countryCode = getCountryAsAlpha2Code(selectedCountry)
    const deliveryDay = bidDate.toISODate() ?? ''

    // Clean error message before fetching new data
    setErrorMessage('')

    try {
      const response = await getMfrrCapacityBid({ countryCode, deliveryDay })

      const blob = new Blob([response.data], {
        type: XLSX_TYPE,
      })

      const url = globalThis.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `mfrr_capacity_bid_${deliveryDay}.xlsx`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      globalThis.URL.revokeObjectURL(url)

      setErrorMessage('')
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 404) {
          pushAlert({
            message: t('bidding.export_mfrr_bid.capacity.download_failed.not_found'),
            severity: 'error',
          })
          return
        }

        if (error.response?.data !== undefined) {
          setErrorMessage(error.response.data)
        } else {
          setErrorMessage(error.message ?? t('bidding.export_mfrr_bid.download_failed.unknown_error'))
        }
      } else {
        setErrorMessage(t('bidding.export_mfrr_bid.download_failed.unknown_error'))
      }
    }
  }

  return (
    <Stack direction="column" spacing={2}>
      {errorMessage != '' && (
        <Alert data-testid="server-error" severity="error">
          <p>{t('bidding.export_mfrr_bid.download_failed.error_message_from_server')}</p>
          <p>{errorMessage}</p>
        </Alert>
      )}

      <Stack alignItems="center" direction="row" spacing={2}>
        <LocalizationProvider adapterLocale={i18n.language} dateAdapter={AdapterLuxon}>
          <DatePicker
            enableAccessibleFieldDOMStructure={false}
            label="Delivery date"
            sx={{ width: 180 }}
            value={bidDate}
            onChange={(newValue) => setBidDate(newValue ?? TOMORROW)}
          />
        </LocalizationProvider>

        <SubmitButton
          buttonProps={{
            startIcon: <DownloadForOfflineOutlined />,
            disabled: isGettingMfrrCapacityBid,
          }}
          dialogProps={dialogProps}
          shouldShowConfirmation={false}
          onSubmit={handleDownloadButtonClick}
        >
          {t('bidding.export_mfrr_bid.capacity.download_button')}
        </SubmitButton>
      </Stack>
    </Stack>
  )
}

export default ExportMfrrCapacityBid
